<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, useSlots } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  conversationLabels: {
    type: Array,
    required: true,
  },
});

const slots = useSlots();
const accountLabels = useMapGetter('labels/getLabels');

const activeLabels = computed(() => {
  return accountLabels.value.filter(({ title }) =>
    props.conversationLabels.includes(title)
  );
});

const showAllLabels = ref(false);
const labelContainer = ref(null);
const hasOverflow = ref(false);
const hiddenCount = ref(0);

const isDark = ref(document.body.classList.contains('dark'));
const fadeBg = ref('#FFFFFF');
let darkObs = null;

function updateFadeBg() {
  isDark.value = document.body.classList.contains('dark');
  if (!labelContainer.value) return;
  let el = labelContainer.value.closest('.conversation');
  if (el) {
    const bg = window.getComputedStyle(el).backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
      fadeBg.value = bg;
      return;
    }
  }
  fadeBg.value = isDark.value ? '#1C1E22' : '#FFFFFF';
}

function checkOverflow() {
  if (!labelContainer.value || showAllLabels.value) return;
  updateFadeBg();
  const inner = labelContainer.value.querySelector('.sz-clabels-inner');
  if (!inner) return;
  const isOverflowing = inner.scrollWidth > inner.clientWidth + 2;
  hasOverflow.value = isOverflowing;

  if (isOverflowing) {
    // Count hidden: find pills that are fully or partially outside
    const pills = Array.from(inner.querySelectorAll('.sz-clabel'));
    const containerRight = inner.getBoundingClientRect().right;
    let visCount = 0;
    for (const pill of pills) {
      const pillRight = pill.getBoundingClientRect().right;
      if (pillRight <= containerRight + 2) visCount++;
      else break;
    }
    hiddenCount.value = Math.max(0, activeLabels.value.length - visCount);
  } else {
    hiddenCount.value = 0;
  }
}

onMounted(() => {
  darkObs = new MutationObserver(() => updateFadeBg());
  darkObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  nextTick(() => {
    const conv = labelContainer.value?.closest('.conversation');
    if (conv) darkObs.observe(conv, { attributes: true, attributeFilter: ['class'] });
    updateFadeBg();
    checkOverflow();
  });
});
onBeforeUnmount(() => { if (darkObs) darkObs.disconnect(); });

const fadeGradient = computed(() => {
  const bg = fadeBg.value;
  if (bg.startsWith('rgb')) {
    const parts = bg.replace(/rgba?\(/, '').replace(')', '').split(',').map(s => s.trim());
    const [r, g, b] = parts;
    return `linear-gradient(270deg, rgba(${r},${g},${b},1) 35%, rgba(${r},${g},${b},0.8) 60%, rgba(${r},${g},${b},0) 100%)`;
  }
  return `linear-gradient(270deg, ${bg} 35%, ${bg}CC 60%, ${bg}00 100%)`;
});

watch(() => props.conversationLabels, () => {
  showAllLabels.value = false;
  nextTick(() => checkOverflow());
}, { deep: true });

const onToggle = e => {
  e.stopPropagation();
  showAllLabels.value = !showAllLabels.value;
  if (!showAllLabels.value) {
    nextTick(() => checkOverflow());
  }
};
</script>

<template>
  <div
    ref="labelContainer"
    v-resize="checkOverflow"
    class="sz-clabels-root"
    :class="{ 'sz-clabels-expanded': showAllLabels }"
  >
    <div class="sz-clabels-inner">
      <slot name="before" />
      <div
        v-for="label in activeLabels"
        :key="label.id"
        class="sz-clabel"
        :style="{
          background: label.color + '1A',
          color: label.color,
        }"
      >
        <span class="sz-clabel-dot" :style="{ background: label.color }" />
        <span class="sz-clabel-title">{{ label.title }}</span>
      </div>
    </div>
    <!-- Fade + Badge -->
    <div
      v-if="hasOverflow && !showAllLabels"
      class="sz-clabels-fade"
      :style="{ background: fadeGradient }"
    >
      <button class="sz-clabels-badge" @click="onToggle">
        <span>+{{ hiddenCount }}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
    <!-- Collapse -->
    <button v-if="showAllLabels && activeLabels.length > 1" class="sz-clabels-badge sz-clabels-collapse-btn" @click="onToggle">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M18 15l-6-6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
  </div>
</template>

<style scoped>
.sz-clabels-root {
  position: relative;
  width: 100%;
  min-width: 0;
  min-height: 20px;
}
.sz-clabels-inner {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
  overflow: hidden;
  height: 20px;
}
.sz-clabels-root.sz-clabels-expanded .sz-clabels-inner {
  flex-wrap: wrap;
  height: auto;
  overflow: visible;
}
.sz-clabel {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px 2px 4px;
  height: 16px;
  border-radius: 29px;
  font-size: 11px;
  font-weight: 500;
  line-height: 100%;
  white-space: nowrap;
  flex-shrink: 0;
}
.sz-clabel-dot {
  width: 9px;
  height: 9px;
  min-width: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}
.sz-clabel-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 120px;
}
/* Fade gradient + badge */
.sz-clabels-fade {
  position: absolute;
  right: 0;
  top: 0;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-left: 48px;
  pointer-events: none;
}
.sz-clabels-badge {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px;
  height: 16px;
  background: #E1EBDC;
  border-radius: 24px;
  font-size: 12px;
  font-weight: 600;
  color: #388E3C;
  line-height: 1;
  border: none;
  cursor: pointer;
  pointer-events: auto;
  flex-shrink: 0;
}
.sz-clabels-collapse-btn {
  margin-top: 4px;
}
.dark .sz-clabels-badge {
  background: rgba(56,142,60,0.15);
  color: #4ADE80;
}
</style>
